import { readRuntimeConfigValue } from "../../common/runtimeConfig";
import type { VerifiedAddress } from "./types";

export type MapboxMapInstance = {
  addControl: (control: unknown, position?: string) => void;
  on: (event: string, handler: (...args: unknown[]) => void) => void;
  remove: () => void;
};

export type MapboxMarkerInstance = {
  setLngLat: (coords: [number, number]) => MapboxMarkerInstance;
  setPopup: (popup: MapboxPopupInstance) => MapboxMarkerInstance;
  addTo: (map: MapboxMapInstance) => MapboxMarkerInstance;
  remove?: () => void;
};

export type MapboxPopupInstance = {
  setText: (text: string) => MapboxPopupInstance;
};

export type MapboxGLNamespace = {
  accessToken: string;
  Map: new (options: Record<string, unknown>) => MapboxMapInstance;
  Marker: new (options?: Record<string, unknown>) => MapboxMarkerInstance;
  Popup: new (options?: Record<string, unknown>) => MapboxPopupInstance;
  NavigationControl: new (options?: Record<string, unknown>) => unknown;
  FullscreenControl: new (options?: Record<string, unknown>) => unknown;
};

declare global {
  interface Window {
    mapboxgl?: MapboxGLNamespace;
  }
}

export const MAPBOX_ACCESS_TOKEN = String(
  readRuntimeConfigValue("REACT_APP_MAPBOX_ACCESS_TOKEN") ||
    process.env.REACT_APP_MAPBOX_ACCESS_TOKEN ||
    ""
).trim();
export const MAPBOX_LANGUAGE = String(
  readRuntimeConfigValue("REACT_APP_MAPBOX_LANGUAGE") ||
    process.env.REACT_APP_MAPBOX_LANGUAGE ||
    navigator.language ||
    "en-US"
).trim();
export const MAPBOX_COUNTRY = String(
  readRuntimeConfigValue("REACT_APP_MAPBOX_COUNTRY") ||
    process.env.REACT_APP_MAPBOX_COUNTRY ||
    ""
).trim();
export const MAPBOX_STYLE_PATH = String(
  readRuntimeConfigValue("REACT_APP_MAPBOX_STYLE_PATH") ||
    process.env.REACT_APP_MAPBOX_STYLE_PATH ||
    "mapbox/streets-v12"
).trim();

const MAPBOX_GL_JS_VERSION = "v3.20.0";
const MAPBOX_GL_SCRIPT_ID = "clinic-onboarding-mapbox-gl-script";
const MAPBOX_GL_STYLES_ID = "clinic-onboarding-mapbox-gl-styles";

let mapboxGlPromise: Promise<MapboxGLNamespace | null> | null = null;

export function hasCoordinates(
  address: VerifiedAddress | null
): address is VerifiedAddress & { latitude: number; longitude: number } {
  return (
    typeof address?.latitude === "number" &&
    Number.isFinite(address.latitude) &&
    typeof address?.longitude === "number" &&
    Number.isFinite(address.longitude)
  );
}

export function buildMapboxStyleUrl() {
  if (!MAPBOX_STYLE_PATH) {
    return "mapbox://styles/mapbox/streets-v12";
  }

  if (MAPBOX_STYLE_PATH.startsWith("mapbox://styles/")) {
    return MAPBOX_STYLE_PATH;
  }

  return `mapbox://styles/${MAPBOX_STYLE_PATH.replace(/^\/+|\/+$/g, "")}`;
}

function ensureMapboxStylesheet() {
  if (typeof document === "undefined") {
    return;
  }

  if (document.getElementById(MAPBOX_GL_STYLES_ID)) {
    return;
  }

  const link = document.createElement("link");
  link.id = MAPBOX_GL_STYLES_ID;
  link.rel = "stylesheet";
  link.href = `https://api.mapbox.com/mapbox-gl-js/${MAPBOX_GL_JS_VERSION}/mapbox-gl.css`;
  document.head.appendChild(link);
}

export async function loadMapboxGlJs() {
  if (!MAPBOX_ACCESS_TOKEN || typeof window === "undefined") {
    return null;
  }

  ensureMapboxStylesheet();

  if (window.mapboxgl?.Map) {
    window.mapboxgl.accessToken = MAPBOX_ACCESS_TOKEN;
    return window.mapboxgl;
  }

  if (mapboxGlPromise) {
    return mapboxGlPromise;
  }

  mapboxGlPromise = new Promise<MapboxGLNamespace | null>((resolve) => {
    const existingScript = document.getElementById(
      MAPBOX_GL_SCRIPT_ID
    ) as HTMLScriptElement | null;

    if (existingScript) {
      existingScript.addEventListener("load", () => {
        if (window.mapboxgl?.Map) {
          window.mapboxgl.accessToken = MAPBOX_ACCESS_TOKEN;
          resolve(window.mapboxgl);
          return;
        }

        resolve(null);
      });
      existingScript.addEventListener("error", () => resolve(null));
      return;
    }

    const script = document.createElement("script");
    script.id = MAPBOX_GL_SCRIPT_ID;
    script.src = `https://api.mapbox.com/mapbox-gl-js/${MAPBOX_GL_JS_VERSION}/mapbox-gl.js`;
    script.async = true;
    script.onload = () => {
      if (window.mapboxgl?.Map) {
        window.mapboxgl.accessToken = MAPBOX_ACCESS_TOKEN;
        resolve(window.mapboxgl);
        return;
      }

      resolve(null);
    };
    script.onerror = () => resolve(null);
    document.head.appendChild(script);
  });

  return mapboxGlPromise;
}

export function buildClinicMapViewHref(address: VerifiedAddress | null) {
  if (!hasCoordinates(address)) {
    return "";
  }

  const params = new URLSearchParams({
    lat: String(address.latitude),
    lng: String(address.longitude),
    label: address.formattedAddress,
  });

  return `/map-view?${params.toString()}`;
}
